import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { MailIcon, LockIcon, EyeIcon, EyeOffIcon, CheckIcon, AlertCircleIcon, ArrowLeftIcon, ShieldCheckIcon, InfoIcon } from 'lucide-react';
import { AuthSplit } from '../../components/auth/AuthSplit';
import { SignupProgress } from '../../components/auth/SignupProgress';
import { Button } from '../../components/ui/Button';
import { Field, Input, inputClasses } from '../../components/ui/Field';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { experiences, experienceById } from '../../data/experiences';
import { institutions } from '../../data/institutions';

/**
 * The Campuzon front door.
 *
 * Four steps, one identity. Step 02 is the point of the whole architecture:
 * choosing an experience adds a membership to the account created in step 01 —
 * it does not create a second account, a second password or a second login.
 *
 * There is no internal-administration option here, and there is no request
 * shape that could produce one: the step only offers the six public
 * experiences, and the authorization layer only accepts those.
 */

const STEPS = [{
  id: 'account',
  label: 'Create account'
}, {
  id: 'experience',
  label: 'Choose how you’ll use Campuzon'
}, {
  id: 'profile',
  label: 'Complete profile'
}, {
  id: 'verify',
  label: 'Verify & continue'
}];

/* A handle is required by the identity model, so signup derives one from the
   email rather than asking for it at the door. You confirm it in step 03. */
function deriveHandle(email: string): string {
  const base = email.split('@')[0].toLowerCase().replace(/[^a-z0-9_]/g, '');
  return (base || 'campuzon').slice(0, 16);
}
export function Signup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const {
    identity,
    authenticated,
    register,
    passwordVerdict,
    usernameVerdict,
    updateProfile,
    pending,
    sendVerification,
    confirmVerification,
    verificationCooldown,
    flagEnabled
  } = useAuth();
  const {
    addExperience,
    workspaces
  } = useWorkspace();

  /* A deep link may preselect the experience; it never skips a step. */
  const deepLink = params.get('role');
  const [step, setStep] = useState(0);
  const [furthest, setFurthest] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [reveal, setReveal] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{
    field?: string;
    message: string;
  } | null>(null);
  const [chosen, setChosen] = useState<string | null>(deepLink && experienceById(deepLink) ? deepLink : null);
  const [fullName, setFullName] = useState('');
  const [handle, setHandle] = useState('');
  const [orgName, setOrgName] = useState('');
  const [institutionId, setInstitutionId] = useState(institutions[0]?.id ?? '');
  const [programme, setProgramme] = useState('');
  const [pendingMembership, setPendingMembership] = useState(false);
  const [code, setCode] = useState('');
  const verdict = useMemo(() => passwordVerdict(password, [email]), [password, email, passwordVerdict]);
  const option = chosen ? experienceById(chosen) : null;
  const advance = (to: number) => {
    setError(null);
    setStep(to);
    setFurthest(current => Math.max(current, to));
  };

  /* ------------------------------------------------------------ step 01 */

  const submitAccount = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError({
        field: 'email',
        message: 'Enter an email address we can actually reach.'
      });
      return;
    }
    if (verdict.problems.length > 0) {
      setError({
        field: 'password',
        message: verdict.problems[0]
      });
      return;
    }
    setBusy(true);
    const result = await register({
      fullName: fullName.trim() || email.split('@')[0],
      username: deriveHandle(email),
      email: email.trim(),
      password,
      countryCode: 'NG',
      marketingOptIn: false
    });
    setBusy(false);
    if (!result.ok) {
      setError({
        field: result.field,
        message: result.error
      });
      return;
    }
    setHandle(result.identity.username);
    setFullName(result.identity.displayName);
    advance(1);
  };

  /* ------------------------------------------------------------ step 02 */

  const submitExperience = () => {
    if (!chosen) {
      setError({
        message: 'Choose how you’ll use Campuzon to continue.'
      });
      return;
    }
    advance(2);
  };

  /* ------------------------------------------------------------ step 03 */

  const submitProfile = (event: React.FormEvent) => {
    event.preventDefault();
    if (!option) return;
    setError(null);
    if (fullName.trim().length < 2) {
      setError({
        field: 'name',
        message: 'Tell us what to call you.'
      });
      return;
    }
    const handleCheck = usernameVerdict(handle);
    if (!handleCheck.ok) {
      setError({
        field: 'handle',
        message: handleCheck.message
      });
      return;
    }
    if (option.organizationKind && orgName.trim().length < 2) {
      setError({
        field: 'org',
        message: `Give your ${option.label.toLowerCase()} a name.`
      });
      return;
    }
    updateProfile({
      displayName: fullName.trim(),
      username: handle,
      usernameKey: handle.toLowerCase(),
      ...(option.id === 'student' ? {
        institutionId,
        programme: programme.trim() || undefined
      } : {})
    });
    const result = addExperience({
      experienceId: option.id,
      organizationName: orgName,
      institutionId: option.id === 'student' || option.id === 'campus' ? institutionId : null
    });
    if (!result.ok) {
      setError({
        message: result.error
      });
      return;
    }
    setPendingMembership(result.pending);
    advance(3);
  };

  /* ------------------------------------------------------------ step 04 */

  const submitCode = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    const result = confirmVerification(code.trim());
    if (!result.ok) {
      setError({
        field: 'code',
        message: result.error ?? 'That code did not match.'
      });
      return;
    }
    finish();
  };
  const finish = () => {
    /* Route by what the account actually holds, resolved server-side — never
       by the experience the person clicked a moment ago. */
    if (workspaces.length === 1) navigate(workspaces[0].path, {
      replace: true
    });else if (workspaces.length > 1) navigate('/workspaces', {
      replace: true
    });else navigate('/', {
      replace: true
    });
  };

  /* An already-signed-in visitor is not offered a second identity. */
  if (authenticated && step === 0) {
    return <AuthSplit data-id="element-8461" data-mp="5hublkmnci1">
        <h1 className="text-h2 font-semibold text-ink" data-id="element-8462" data-mp="4zs88baa427">You are already signed in</h1>
        <p className="mt-3 text-body text-ink-muted" data-id="element-8463" data-mp="56fchy5n06jj">
          @{identity?.username} is signed in on this device. Campuzon gives one person one account —
          to do something new here, add an experience to the account you have rather than making a
          second one.
        </p>
        <div className="mt-6 flex flex-wrap gap-3" data-id="element-8464" data-mp="2sd1z8w2cwhs1">
          <Button variant="primary" onClick={() => navigate('/workspaces')} data-id="element-8465" data-mp="2sbquasx6d3zc">
            Add an experience
          </Button>
          <Button variant="secondary" onClick={() => navigate('/')} data-id="element-8466" data-mp="20mdz9ncf5ify">
            Back to Campuzon
          </Button>
        </div>
      </AuthSplit>;
  }
  if (!flagEnabled('registration')) {
    return <AuthSplit data-id="element-8467" data-mp="29fh9lmrrpf6d">
        <h1 className="text-h2 font-semibold text-ink" data-id="element-8468" data-mp="29g0sp6t5m9lz">Registration is closed right now</h1>
        <p className="mt-3 text-body text-ink-muted" data-id="element-8469" data-mp="24nfzo7i2o0ji">
          New accounts are paused by an administrator. Existing accounts are unaffected.
        </p>
        <Link to="/signin" className="mt-6 inline-block" data-id="element-8470" data-mp="1cxb89ovwad8s">
          <Button variant="primary" data-id="element-8471" data-mp="1cwtfj28fyh5m">Sign in instead</Button>
        </Link>
      </AuthSplit>;
  }
  return <AuthSplit aside={<span className="text-ink-muted" data-id="element-8473" data-mp="26r724wnn26ac">
          Already have an account?{' '}
          <Link to="/signin" className="font-semibold text-brand-700 underline-offset-2 hover:underline" data-id="element-8474" data-mp="26tb59cdpr19k">
            Sign in
          </Link>
        </span>} data-id="element-8472" data-mp="21tyhmpf0pq5x">
      <SignupProgress steps={STEPS} current={step} furthest={furthest} onSelect={setStep} data-id="element-8475" data-mp="2fpipmpt7knyk" />

      <div className="mt-8" data-id="element-8476" data-mp="2kovj3tel4ta4">
        {/* ============================================== 01 create account */}
        {step === 0 && <form noValidate onSubmit={submitAccount} data-id="element-8477" data-mp="2ug9im16jdt39">
            <h1 className="text-[clamp(1.6rem,3vw,2.1rem)] font-semibold leading-tight text-ink" data-id="element-8478" data-mp="2uivyco3kzewk">
              Welcome to Campuzon
            </h1>
            <p className="mt-3 text-body text-ink-muted" data-id="element-8479" data-mp="33i8yur9q8t55">
              Create your Campuzon Identity to get started. You’ll choose how you want to use
              Campuzon next.
            </p>

            <div className="mt-6 space-y-5" data-id="element-8480" data-mp="3ikr7wyecjpy5">
              <Field label="Email address" htmlFor="signup-email" data-id="element-8481" data-mp="3ilsg8v3z9yc6">
                <div className="relative" data-id="element-8482" data-mp="3dkcvl45pptub">
                  <MailIcon aria-hidden className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-ink-subtle" data-id="element-8483" data-mp="3dnr1unhhyn71" />
                  <Input id="signup-email" type="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={event => setEmail(event.target.value)} aria-invalid={error?.field === 'email'} aria-describedby={error?.field === 'email' ? 'signup-email-error' : undefined} className="pl-11" data-id="element-8484" data-mp="55l45xp8f5dz" />
                </div>
                {error?.field === 'email' && <InlineError id="signup-email-error" text={error.message} data-id="element-8485" data-mp="j3sxlvn27oke" />}
              </Field>

              <Field label="Password" htmlFor="signup-password" hint="Use 8 or more characters with a mix of letters, numbers and symbols." data-id="element-8486" data-mp="1ypp59dnmkmp3">
                <div className="relative" data-id="element-8487" data-mp="27retkam7n5in">
                  <LockIcon aria-hidden className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-ink-subtle" data-id="element-8488" data-mp="22r0n2x09c3mq" />
                  <Input id="signup-password" type={reveal ? 'text' : 'password'} autoComplete="new-password" placeholder="Create a strong password" value={password} onChange={event => setPassword(event.target.value)} aria-invalid={error?.field === 'password'} aria-describedby="signup-password-strength" className="pl-11 pr-12" data-id="element-8489" data-mp="2cpgel1375ewr" />
                  <button type="button" onClick={() => setReveal(current => !current)} aria-pressed={reveal} className="absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-lg text-ink-subtle transition-colors duration-150 ease-out hover:text-ink" data-id="element-8490" data-mp="3f2j5cr87lmwo">
                    {reveal ? <EyeOffIcon aria-hidden className="h-[18px] w-[18px]" data-id="element-8491" data-mp="2w8ne5phu3295" /> : <EyeIcon aria-hidden className="h-[18px] w-[18px]" data-id="element-8492" data-mp="313jjfrc2zm47" />}
                    <span className="sr-only" data-id="element-8493" data-mp="2mag0bfiud0bd">{reveal ? 'Hide password' : 'Show password'}</span>
                  </button>
                </div>
                <p id="signup-password-strength" aria-live="polite" className="sr-only" data-id="element-8494" data-mp="2dbcieeveuqyg">
                  {password ? `${verdict.label}. ${verdict.problems.join(' ')}` : ''}
                </p>
                {password && <div className="mt-2 flex items-center gap-2" data-id="element-8495" data-mp="nvijfhxkkg2p">
                    <span className="flex flex-1 gap-1" aria-hidden data-id="element-8496" data-mp="j3ibp3b9civk">
                      {[0, 1, 2, 3].map(index => <span key={index} className={`h-1.5 flex-1 rounded-full ${index < verdict.score ? 'bg-brand-700' : 'bg-line'}`} data-id="element-8497" data-mp="e5p8mj5944zi" />)}
                    </span>
                    <span className="text-caption text-ink-muted" data-id="element-8498" data-mp="11vb6925z6c5e">
                      {verdict.problems[0] ?? verdict.label}
                    </span>
                  </div>}
                {error?.field === 'password' && <InlineError text={error.message} data-id="element-8499" data-mp="sw6bm6xjb2yi" />}
              </Field>

              <label className="flex items-start gap-3 text-body-sm text-ink" data-id="element-8500" data-mp="3hmz3yzstc8p9">
                <input type="checkbox" checked={consent} onChange={event => setConsent(event.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 rounded border-line-strong text-brand-700 focus-visible:outline-none" data-id="element-8501" data-mp="3dj9i4i3iw57x" />
                <span data-id="element-8502" data-mp="22158k0aluv04">
                  I agree to the{' '}
                  <Link to="/legal/terms" className="font-medium text-brand-700 hover:underline" data-id="element-8503" data-mp="26tydop9zk5cq">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/legal/privacy" className="font-medium text-brand-700 hover:underline" data-id="element-8504" data-mp="2gr30mrbno4ul">
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>

              {error && !error.field && <InlineError text={error.message} data-id="element-8505" data-mp="2prho9xc3dw19" />}

              <Button type="submit" variant="primary" block disabled={!consent} loading={busy} data-id="element-8506" data-mp="qfolfk2mhwgm">
                {busy ? 'Creating your account…' : 'Create account'}
              </Button>

              {/*
                No Google / Apple / Microsoft buttons are rendered, because no
                provider is configured. A button that opens nothing is worse
                than its absence, so we say why instead of faking it.
               */}
              <p className="flex items-start gap-2 rounded-xl border border-line bg-canvas p-3 text-caption text-ink-muted" data-id="element-8507" data-mp="a1hhn7elwwrs">
                <InfoIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-8508" data-mp="3dizeykwr6ldy" />
                <span data-id="element-8509" data-mp="3dgvd08iezwgb">
                  Google, Apple and Microsoft sign-in are built but not switched on — no provider is
                  connected yet. They will appear here once one is, rather than as buttons that
                  cannot work.
                </span>
              </p>
            </div>
          </form>}

        {/* ========================================== 02 choose experience */}
        {step === 1 && <div data-id="element-8510" data-mp="2ptwyvhhrp0lk">
            <h1 className="text-[clamp(1.6rem,3vw,2.1rem)] font-semibold leading-tight text-ink" data-id="element-8511" data-mp="2psc4pszd7jo8">
              How will you use Campuzon?
            </h1>
            <p className="mt-3 text-body text-ink-muted" data-id="element-8512" data-mp="159jfyd5bi31y">
              Choose what you’re here to do. This adds a role to the account you just created — you
              can add more experiences later without another password.
            </p>

            <div role="radiogroup" aria-label="Campuzon experiences" className="mt-6 grid gap-3 sm:grid-cols-2" data-id="element-8513" data-mp="10db5ve5z901n">
              {experiences.map(item => {
            const selected = chosen === item.id;
            return <button key={item.id} type="button" role="radio" aria-checked={selected} onClick={() => setChosen(item.id)} className={`flex min-h-[132px] flex-col items-start rounded-2xl border p-4 text-left transition-colors duration-150 ease-out ${selected ? 'border-brand-700 bg-brand-50 ring-1 ring-brand-700' : 'border-line bg-surface hover:border-line-strong hover:bg-canvas'}`} data-id="element-8514" data-mp="1f1xoboqwi7x3">
                    <span className="flex w-full items-start justify-between gap-2" data-id="element-8515" data-mp="20ivg8j6051o1">
                      <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${selected ? 'bg-brand-700 text-white' : 'bg-canvas text-ink-muted'}`} data-id="element-8516" data-mp="1vkbvgm359vx9">
                        <item.icon aria-hidden className="h-5 w-5" data-id="element-8517" data-mp="1hlc2j28lmuva" />
                      </span>
                      {selected && <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-700" data-id="element-8518" data-mp="1cnsqm8zdiu6i">
                          <CheckIcon aria-hidden className="h-3.5 w-3.5 text-white" data-id="element-8519" data-mp="3bz3alxba56om" />
                        </span>}
                    </span>
                    <span className="mt-3 block text-body font-semibold text-ink" data-id="element-8520" data-mp="3fi2cg9gci6wp">{item.label}</span>
                    <span className="mt-1 block text-caption leading-snug text-ink-muted" data-id="element-8521" data-mp="3jickdrnp77e7">
                      {item.description}
                    </span>
                  </button>;
          })}
            </div>

            {error && <InlineError text={error.message} data-id="element-8522" data-mp="3t85t8kwgh8er" />}

            <div className="mt-6 flex flex-wrap gap-3" data-id="element-8523" data-mp="2wj6ovgyzsqru">
              <Button variant="primary" onClick={submitExperience} disabled={!chosen} data-id="element-8524" data-mp="2wkrj15hea7p6">
                Continue
              </Button>
              <Button variant="ghost" icon={<ArrowLeftIcon className="h-4 w-4" data-id="element-8526" data-mp="35ilbu6fyaj7l" />} onClick={() => setStep(0)} data-id="element-8525" data-mp="35itpi0jwdsju">
                Back
              </Button>
            </div>
          </div>}

        {/* ============================================ 03 complete profile */}
        {step === 2 && option && <form noValidate onSubmit={submitProfile} data-id="element-8527" data-mp="prfhrqi3u35o">
            <h1 className="text-[clamp(1.6rem,3vw,2.1rem)] font-semibold leading-tight text-ink" data-id="element-8528" data-mp="pu1r9vok2sqj">
              {option.profileLabel}
            </h1>
            <p className="mt-3 text-body text-ink-muted" data-id="element-8529" data-mp="16vpcnnon6u7n">{option.profileHint}</p>

            <div className="mt-6 space-y-5" data-id="element-8530" data-mp="1kmm1d3fg6r9f">
              <Field label="Your name" htmlFor="signup-name" data-id="element-8531" data-mp="1kndqpwigbsr9">
                <Input id="signup-name" value={fullName} autoComplete="name" onChange={event => setFullName(event.target.value)} aria-invalid={error?.field === 'name'} className={inputClasses} data-id="element-8532" data-mp="1fv45uuw71ivu" />
                {error?.field === 'name' && <InlineError text={error.message} data-id="element-8533" data-mp="23iu9qg3rbi8y" />}
              </Field>

              <Field label="Your handle" htmlFor="signup-handle" hint="How people find you on Campuzon. Your account is identified internally by an id that never changes, so this stays yours to edit." data-id="element-8534" data-mp="1ymdc8vao3rj7">
                <div className="relative" data-id="element-8535" data-mp="2fuwei6ludcxf">
                  <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-body text-ink-subtle" data-id="element-8536" data-mp="2fvy0v78b5lug">
                    @
                  </span>
                  <Input id="signup-handle" value={handle} onChange={event => setHandle(event.target.value.replace(/\s/g, ''))} aria-invalid={error?.field === 'handle'} className="pl-8" data-id="element-8537" data-mp="2722ww2ixa1ge" />
                </div>
                {error?.field === 'handle' && <InlineError text={error.message} data-id="element-8538" data-mp="1o49t6y0h5nhy" />}
              </Field>

              {option.organizationKind && <Field label={`${option.label} name`} htmlFor="signup-org" hint="This creates an organization. You are its first member — colleagues join it later on their own accounts." data-id="element-8539" data-mp="1s4tr43svgv55">
                  <Input id="signup-org" value={orgName} onChange={event => setOrgName(event.target.value)} aria-invalid={error?.field === 'org'} className={inputClasses} data-id="element-8540" data-mp="3mha3lmc9h5ig" />
                  {error?.field === 'org' && <InlineError text={error.message} data-id="element-8541" data-mp="21r5qrdjp3829" />}
                </Field>}

              {(option.id === 'student' || option.id === 'campus') && <Field label="Institution" htmlFor="signup-institution" data-id="element-8542" data-mp="2pex1wdti1f1y">
                  <select id="signup-institution" value={institutionId} onChange={event => setInstitutionId(event.target.value)} className={inputClasses} data-id="element-8543" data-mp="2uerrme1jfylf">
                    {institutions.map(item => <option key={item.id} value={item.id} data-id="element-8544" data-mp="1423ups5ymumz">
                        {item.name}
                      </option>)}
                  </select>
                </Field>}

              {option.id === 'student' && <Field label="Programme" htmlFor="signup-programme" hint="Optional. You can add it later from your profile." data-id="element-8545" data-mp="3ic7mz9esv9ws">
                  <Input id="signup-programme" value={programme} placeholder="B.Sc. Systems Engineering" onChange={event => setProgramme(event.target.value)} className={inputClasses} data-id="element-8546" data-mp="3s9cc9i3xzlht" />
                </Field>}

              <p className="flex items-start gap-2 rounded-xl border border-line bg-canvas p-3 text-caption text-ink-muted" data-id="element-8547" data-mp="3ddmnxdbaaw9x">
                <ShieldCheckIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-8548" data-mp="2kw0une1iwid9" />
                <span data-id="element-8549" data-mp="2ktxsxwr0d0sp">{option.verification}</span>
              </p>

              {error && !error.field && <InlineError text={error.message} data-id="element-8550" data-mp="2gsclws15ngqf" />}

              <div className="flex flex-wrap gap-3" data-id="element-8551" data-mp="juks7kcdtljf">
                <Button type="submit" variant="primary" data-id="element-8552" data-mp="jrq6lphmtuof">
                  Save and continue
                </Button>
                <Button type="button" variant="ghost" icon={<ArrowLeftIcon className="h-4 w-4" data-id="element-8554" data-mp="yipcgsr3254b" />} onClick={() => setStep(1)} data-id="element-8553" data-mp="tjmbbje84j1m">
                  Back
                </Button>
              </div>
            </div>
          </form>}

        {/* ============================================= 04 verify & finish */}
        {step === 3 && <form noValidate onSubmit={submitCode} data-id="element-8555" data-mp="as35z5wq8swf">
            <h1 className="text-[clamp(1.6rem,3vw,2.1rem)] font-semibold leading-tight text-ink" data-id="element-8556" data-mp="avh7yd1hb03c">
              Check your email
            </h1>
            <p className="mt-3 text-body text-ink-muted" data-id="element-8557" data-mp="1m78beho8t5jq">
              We sent a six-digit code to <span className="font-semibold text-ink" data-id="element-8558" data-mp="1r6l8rwcqpv0k">{email}</span>. It
              expires in fifteen minutes and can only be used once.
            </p>

            {pendingMembership && option && <p className="mt-4 flex items-start gap-2 rounded-xl border border-warn-100 bg-warn-50 p-3 text-caption text-ink" data-id="element-8559" data-mp="36q2cvs3avlzz">
                <AlertCircleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-warn-700" data-id="element-8560" data-mp="31y14ih996ff8" />
                <span data-id="element-8561" data-mp="2wzzknu406c2p">
                  Your {option.label.toLowerCase()} workspace is waiting on confirmation, so it will
                  not open yet. Your account works either way — nothing is blocked behind it.
                </span>
              </p>}

            <div className="mt-6 space-y-5" data-id="element-8562" data-mp="2izybzshhy0co">
              <Field label="Verification code" htmlFor="signup-code" data-id="element-8563" data-mp="2j2u8dcdp4gz0">
                <Input id="signup-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={event => setCode(event.target.value.replace(/\D/g, ''))} aria-invalid={error?.field === 'code'} className="cz-num tracking-[0.4em]" data-id="element-8564" data-mp="2e2yc5zb8ptu0" />
                {error?.field === 'code' && <InlineError text={error.message} data-id="element-8565" data-mp="2w42ethmucqq4" />}
              </Field>

              {/* Preview build: the code is shown because no mail provider is
                  connected. Production sends it only to the inbox. */}
              {pending?.code && <p className="rounded-xl border border-line bg-canvas p-3 text-caption text-ink-muted" data-id="element-8566" data-mp="2mczguop7krv2">
                  Preview build — the code is{' '}
                  <span className="cz-num font-semibold text-ink" data-id="element-8567" data-mp="2de47x4us53o2">{pending.code}</span>. In
                  production this only reaches your inbox.
                </p>}

              <div className="flex flex-wrap gap-3" data-id="element-8568" data-mp="23fokbbuyocjb">
                <Button type="submit" variant="primary" disabled={code.length < 6} data-id="element-8569" data-mp="23fy7kromg5lb">
                  Verify and continue
                </Button>
                <Button type="button" variant="secondary" disabled={verificationCooldown > 0} onClick={() => sendVerification('email')} data-id="element-8570" data-mp="1osg05ljocwv7">
                  {verificationCooldown > 0 ? `Resend in ${verificationCooldown}s` : 'Resend the code'}
                </Button>
              </div>

              <button type="button" onClick={finish} className="text-body-sm font-medium text-ink-muted underline-offset-2 hover:text-ink hover:underline" data-id="element-8571" data-mp="27qsdljhmcswl">
                Continue and verify later
              </button>
            </div>
          </form>}
      </div>
    </AuthSplit>;
}
function InlineError({
  text,
  id
}: {
  text: string;
  id?: string;
}) {
  return <p id={id} role="alert" className="mt-2 flex items-start gap-1.5 text-caption font-medium text-danger-700" data-id="element-8572" data-mp="2rft0z38ukulu">
      <AlertCircleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-8573" data-mp="2tjo8gzmbmzkj" />
      <span data-id="element-8574" data-mp="2thlc7xuksa7d">{text}</span>
    </p>;
}